import { DATA_KEY } from '../constants/utils';
import { DotProps } from 'recharts';
import { IParsedChartDataRecord } from '../interfaces';
import { isNumber } from 'lodash';
import { CHART_TYPES } from '../constants/chartTypes';
import { ValueOf } from 'ts-essentials';
import SvgIcon from '../../Icon/SvgIcon';
interface IProps extends DotProps {
  line: {
    name: string;
    color: string;
  };
  xAxisKey?: string;
  onDotClick?: (data: {
    calculatedValue: number;
    cy: number;
    cx: number;
    name: string;
    color: string;
    xAxisKey: string;
    xAxisValue: string;
  }) => void;
  payload: Record<string, string>;
  // Recharts types returns ANY for value
  value?: any;
  // only available when the DOT is not hover or clicked (Case 2 and 3 in Doc)
  // <Line.dot /> field should always pass the data to check isLastDataPointInLine
  data?: IParsedChartDataRecord[];
  chartType: CHART_TYPES;
  opacity?: number;
  active?: boolean;
}

const isChartDataRecordNotNull = (value: ValueOf<IParsedChartDataRecord>) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return true;
  }

  if (value === 'string') {
    return isNumber(value);
  }
};

/**
 * CustomLineDot component is used to display dots on the line chart.
 * It is used in two diff cases:
 * 1. Displaying dots when mouse over lines.
 * 2. When a dot is clicked in chart with activeDot prop in <Line />,
 *    it will activate the dot and open the tooltip with the data.
 * 3. A dot might be connected with nulls on both sides, then we need to display the dot
 */
const CustomLineDot = ({
  cx,
  cy,
  line,
  onMouseOver,
  onMouseLeave,
  value,
  onDotClick,
  payload,
  xAxisKey = DATA_KEY,
  data,
  chartType,
  opacity,
  active,
}: IProps) => {
  const payloadKey = Object.keys(payload)[0];
  // For case 3: if the data point is connected with nulls on any sides for Expression Line Chart
  let isDotConnectedWithNulls = false;
  if (
    data &&
    (chartType === CHART_TYPES.LINE_CHART ||
      chartType === CHART_TYPES.RETENTION_LINE)
  ) {
    const xAxisValue = payload[payloadKey];
    const xAxisValueIndex = data.findIndex((d) => d[xAxisKey] === xAxisValue);
    if (xAxisValueIndex !== -1) {
      // if the date point is on index 0, always treat the prev datapoint as null.
      // Otherwise, we cannot display the data: [1, null, 2]
      // same for the last data point.
      const isPrevDataPointNull =
        !isChartDataRecordNotNull(data?.[xAxisValueIndex - 1]?.[line.name]) ||
        xAxisValueIndex === 0;
      const isNextDataPointNull =
        !isChartDataRecordNotNull(data?.[xAxisValueIndex + 1]?.[line.name]) ||
        xAxisValueIndex === data.length - 1;
      isDotConnectedWithNulls = isPrevDataPointNull && isNextDataPointNull;
    }
  }

  const displayRegularDotsOnLine =
    !data || isDotConnectedWithNulls || data.length === 1 || active;
  return !!cx && !!cy && displayRegularDotsOnLine ? (
    <svg
      x={cx - 7}
      y={cy - 7}
      style={{ opacity, cursor: onMouseOver ? 'pointer' : 'default' }}
    >
      <SvgIcon
        name="line-dot"
        width={14}
        height={14}
        fill={line.color}
        onClick={() => {
          const { name, color } = line;
          const { [xAxisKey]: xAxisValue } = payload;
          if (onDotClick) {
            onDotClick({
              calculatedValue: value,
              cy: cy!,
              cx: cx!,
              name,
              color,
              xAxisKey,
              xAxisValue,
            });
          }
        }}
        // cast MouseEventHandler<DotProps, SVGCircleElement> to MouseEventHandler<SVGSVGElement>
        onMouseOver={onMouseOver as React.MouseEventHandler<SVGSVGElement>}
        onMouseOut={onMouseLeave as React.MouseEventHandler<SVGSVGElement>}
      />
    </svg>
  ) : (
    <></>
  );
};

export default CustomLineDot;
